/**
 * CompanyAccessPanel
 *
 * Owner-facing view of CompanyAccessGrant rows across every company the
 * signed-in user owns. Pending requests can be approved or declined; active
 * grants can have their level changed or be revoked. Authority is the
 * Company.owner field — the backend asserts it on every mutation.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { companiesAPI } from '../../services/api';
import { useUI } from '../../context/UIContext';
import ConfirmDialog from './ConfirmDialog';
import Button from './Button';
import Icon from './Icon';
import { Select } from './FormControls';

const ACCESS_LEVELS = [
  { value: 'read', label: 'Read only' },
  { value: 'read_write', label: 'Read & write' },
  { value: 'admin', label: 'Admin' },
];
const ACCESS_LABEL = Object.fromEntries(ACCESS_LEVELS.map((l) => [l.value, l.label]));

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

function initials(name) {
  return (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('') || '?';
}

export default function CompanyAccessPanel({ onCountChange }) {
  const { showToast } = useUI();
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);   // grant _id currently mutating
  const [revoking, setRevoking] = useState(null); // grant awaiting revoke confirm

  const fetchGrants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await companiesAPI.access.requests();
      if (res?.success) setGrants(res.data || []);
      else showToast(res?.message || 'Failed to load access requests', 'error');
    } catch (err) {
      showToast(err?.message || 'Failed to load access requests', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchGrants(); }, [fetchGrants]);

  // Keep the parent's "Company Access" tab badge in sync with what's loaded.
  useEffect(() => {
    if (onCountChange) onCountChange(grants.length);
  }, [grants, onCountChange]);

  const { pending, active } = useMemo(() => ({
    pending: grants.filter((g) => g.status === 'pending'),
    active: grants.filter((g) => g.status === 'active'),
  }), [grants]);

  const runAction = useCallback(async (grant, fn, okMessage) => {
    setBusyId(grant._id);
    try {
      const res = await fn();
      if (res?.success) {
        showToast(okMessage, 'success');
        await fetchGrants();
        return true;
      }
      showToast(res?.message || 'Action failed', 'error');
    } catch (err) {
      showToast(err?.message || 'Action failed', 'error');
    } finally {
      setBusyId(null);
    }
    return false;
  }, [showToast, fetchGrants]);

  const handleApprove = useCallback((grant) =>
    runAction(
      grant,
      () => companiesAPI.access.approve(grant.company._id, grant._id),
      `Access granted to ${grant.user?.fullName || 'user'}`
    ), [runAction]);

  const handleDeny = useCallback((grant) =>
    runAction(
      grant,
      () => companiesAPI.access.deny(grant.company._id, grant._id),
      'Access request declined'
    ), [runAction]);

  const handleLevelChange = useCallback((grant, accessLevel) => {
    if (accessLevel === grant.accessLevel) return;
    runAction(
      grant,
      () => companiesAPI.access.setLevel(grant.company._id, grant._id, accessLevel),
      'Access level updated'
    );
  }, [runAction]);

  const handleRevoke = useCallback(async () => {
    if (!revoking) return;
    const ok = await runAction(
      revoking,
      () => companiesAPI.access.revoke(revoking.company._id, revoking._id),
      'Access revoked'
    );
    if (ok) setRevoking(null);
  }, [revoking, runAction]);

  /* ── Identity block (avatar + name + company) ─────────────────────────── */
  const Identity = ({ grant }) => (
    <div className="flex items-start gap-3 min-w-0">
      <div
        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-bold"
        style={{ background: '#e4f5f3', color: '#145f5f' }}
      >
        {initials(grant.user?.fullName)}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-[13px] text-[#08111f]">
            {grant.user?.fullName || 'Unknown user'}
          </span>
          {grant.user?.roleName && (
            <span
              className="px-1.5 py-[1px] rounded-[4px] text-[9.5px] font-semibold uppercase tracking-[0.5px]"
              style={{ background: '#edeef3', color: '#3a4a66' }}
            >
              {grant.user.roleName}
            </span>
          )}
        </div>
        <div className="text-[11px] truncate" style={{ color: '#7a7060' }}>
          {grant.user?.email || '—'}
          {grant.user?.profession ? ` · ${grant.user.profession}` : ''}
        </div>
        <div className="text-[11px] mt-[2px] text-[#08111f]">
          {grant.company?.name || 'Company'}
          {grant.company?.cin && (
            <span className="ml-1" style={{ color: '#7a7060', fontFamily: "'JetBrains Mono', monospace" }}>
              {grant.company.cin}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-[12px] border px-5 py-12 text-center text-[13px]"
        style={{ borderColor: '#ddd6c6', color: '#7a7060' }}>
        Loading access requests…
      </div>
    );
  }

  if (grants.length === 0) {
    return (
      <div className="bg-white rounded-[12px] border px-5 py-14 text-center"
        style={{ borderColor: '#ddd6c6' }}>
        <div className="flex justify-center mb-2" style={{ color: '#b3a995' }}>
          <Icon name="unlock" size={28} />
        </div>
        <div className="text-[14px] font-semibold text-[#08111f] mb-1">No access requests</div>
        <div className="text-[12px]" style={{ color: '#7a7060' }}>
          When someone adds a company you own, their request to collaborate shows up here.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Pending requests ───────────────────────────────────────────── */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[1px] mb-2" style={{ color: '#7a7060' }}>
          Pending Requests ({pending.length})
        </div>
        {pending.length === 0 ? (
          <div className="text-[12px] px-1 py-3" style={{ color: '#7a7060' }}>
            No requests waiting on you.
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map((grant) => (
              <div
                key={grant._id}
                className="bg-white rounded-[10px] border p-4 flex items-center justify-between gap-4 flex-wrap"
                style={{ borderColor: '#ddd6c6' }}
              >
                <Identity grant={grant} />
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[11px] mr-1" style={{ color: '#7a7060' }}>
                    Requested {formatDate(grant.requestedAt)}
                  </span>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={busyId === grant._id}
                    onClick={() => handleApprove(grant)}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={busyId === grant._id}
                    onClick={() => handleDeny(grant)}
                  >
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Active access ──────────────────────────────────────────────── */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[1px] mb-2" style={{ color: '#7a7060' }}>
          Active Access ({active.length})
        </div>
        {active.length === 0 ? (
          <div className="text-[12px] px-1 py-3" style={{ color: '#7a7060' }}>
            No one currently holds granted access.
          </div>
        ) : (
          <div className="space-y-2">
            {active.map((grant) => (
              <div
                key={grant._id}
                className="bg-white rounded-[10px] border p-4 flex items-center justify-between gap-4 flex-wrap"
                style={{ borderColor: '#ddd6c6' }}
              >
                <Identity grant={grant} />
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-[152px]">
                    <Select
                      size="sm"
                      title="Change access level"
                      value={grant.accessLevel}
                      disabled={busyId === grant._id}
                      onChange={(v) => handleLevelChange(grant, v)}
                      options={ACCESS_LEVELS}
                    />
                  </div>
                  <span className="text-[11px]" style={{ color: '#7a7060' }}>
                    since {formatDate(grant.grantedAt)}
                  </span>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={busyId === grant._id}
                    onClick={() => setRevoking(grant)}
                  >
                    Revoke
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(revoking)}
        onClose={() => setRevoking(null)}
        variant="danger"
        title="Revoke access?"
        message={revoking ? (
          <>
            Revoke <strong>{revoking.user?.fullName || 'this user'}</strong>'s{' '}
            {ACCESS_LABEL[revoking.accessLevel] || revoking.accessLevel} access to{' '}
            <strong>{revoking.company?.name || 'this company'}</strong>?
          </>
        ) : ''}
        note="They lose access immediately. They can request access again later."
        confirmLabel="Revoke access"
        cancelLabel="Cancel"
        onConfirm={handleRevoke}
      />
    </div>
  );
}
